import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { ForcedCheckinResult } from '../shared/forced-checkin-result';
import { PlantForcedCheckin } from '../shared/plant-forced-checkin';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';
import { Markers } from '../../../shared/markers';
import { LoaderService } from '../../../shared/services/loader.service';
import { GeolocationService } from '../../../shared/services/geolocation.service';
import { ClusterIconStyle } from '@google/markerclustererplus';
import { MapsAPILoader } from '@agm/core';
import LatLngLiteral = google.maps.LatLngLiteral;
import MapTypeStyle = google.maps.MapTypeStyle;
import Icon = google.maps.Icon;

@Component({
  selector: 'app-forced-checkin-map',
  templateUrl: './forced-checkin-map.component.html',
  styleUrls: ['./forced-checkin-map.component.scss']
})
export class ForcedCheckinMapComponent implements OnInit, OnChanges, OnDestroy {

  @Input() showBucket: boolean;
  @Input() forcedCheckinResults: ForcedCheckinResult[] = [];
  @Input() plantForcedCheckins: PlantForcedCheckin[] = [];
  @Input() selectedPlantGlid: string = null;
  @Input() selectedPlantRadius: number = null;

  @Output() selectedPlantGlidChange: EventEmitter<String> = new EventEmitter<String>();
  @Output() showBucketChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public readonly MAX_Z_INDEX = 100;
  private defaultLat  = 47.988212705125434;
  private defaultLng  = 13.40030886232853;
  private defaultZoom = 5;

  lat  = this.defaultLat;
  lng  = this.defaultLng;
  zoom = this.defaultZoom;

  selectedPlantLatitude: number = null;
  selectedPlantLongitude: number = null;

  latLngLiteralSubject: Subject<LatLngLiteral> = new Subject<LatLngLiteral>();
  zoomSubject: Subject<number>          = new Subject<number>();

  private latLngLiteralSubscription: Subscription;
  private zoomSubscription: Subscription;

  mapStyles: Array<MapTypeStyle> = [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'off'
        }
      ]
    }
  ];

  clusterStyle: Array<ClusterIconStyle> = [{
    url: Markers.CLASSIC,
    height: 51.2,
    width: 51.2,
    className: 'cluster-marker',
    anchorIcon: [51.2, 25.6],
    anchorText: [14, 0.5],
    textColor: '#000',
    textSize: 10
  }];

  plantMarker = Markers.CLASSIC_SERVICE_CENTER;
  classicMarker = Markers.CLASSIC;
  checkinMarker = Markers.PIN;

  plantMarkerIcon: Icon;
  classicMarkerIcon: Icon;
  checkinMarkerIcon: Icon;

  constructor(
    private geolocationService: GeolocationService,
    private loader: LoaderService,
    private cdr: ChangeDetectorRef,
    private mapsApiLoader: MapsAPILoader,
  ) {
    this.mapsApiLoader.load().then(() => {
      this.plantMarkerIcon = {url: this.plantMarker, scaledSize: new google.maps.Size(60, 60)};
      this.classicMarkerIcon = {url: this.classicMarker, scaledSize: new google.maps.Size(51.2, 51.2), labelOrigin: new google.maps.Point(25.6, 19.6)};
      this.checkinMarkerIcon = {url: this.checkinMarker, scaledSize: new google.maps.Size(40, 40)};
    });

    this.latLngLiteralSubscription = this.latLngLiteralSubject.pipe(
      debounceTime(1000)
    ).subscribe((latLngLiteral: LatLngLiteral) => {
      this.lat = latLngLiteral.lat;
      this.lng = latLngLiteral.lng;
      this.cdr.detectChanges();
    });

    this.zoomSubscription = this.zoomSubject.pipe(
      debounceTime(1000)
    ).subscribe(zoom => {
      this.zoom = zoom;
      this.cdr.detectChanges();
    });
  }

  ngOnInit(): void {
    this.loader.startLoad(false);
    this.geolocationService.ipGeoloc()
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(result => {
        if (result.result && this.forcedCheckinResults.length === 0 && this.plantForcedCheckins.length === 0) {
          this.latLngLiteralSubject.next({ lat: result.result.latitude, lng: result.result.longitude });
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.forcedCheckinResults.length > 0 && changes.plantForcedCheckins && changes.plantForcedCheckins.currentValue != null) {
      const plant = this.forcedCheckinResults.find(res => res.glid === this.selectedPlantGlid);
      this.selectedPlantLatitude = plant.latitude;
      this.selectedPlantLongitude = plant.longitude;
    }
  }

  public centerChanged(latlng: LatLngLiteral): void {
    this.latLngLiteralSubject.next(latlng);
  }

  public zoomChanged(zoom: number): void {
    this.zoomSubject.next(zoom);
  }

  public selectPlant(glid: string): void {
    this.selectedPlantGlidChange.emit(glid);
  }

  ngOnDestroy(): void {
    this.latLngLiteralSubscription.unsubscribe();
    this.zoomSubscription.unsubscribe();
  }
}
